import { call, put, takeLatest } from "redux-saga/effects"

import { apiGeneratorBase } from "../../Utils/saga"
import Creators, { DashboardTypes as constants } from "./reducer"
import * as services from "./services"

// The networks run in a fixed order rather than by volume. RDR carries the biggest and
// straightest flow, so it sits on top and its ribbon runs across the diagram without
// crossing anything; CDRN and Ethoca fan out beneath it. Sorting by count would reshuffle
// the whole picture every time the window changed.
const SERVICE_ORDER = ["rdr", "cdrn", "ethoca"]

// Shape the already-aggregated source -> outcome rows (CHA1-2007: one row per
// alert_service x alert_status, with its own count/volume - no client-side tallying of
// raw alerts anymore) into the nodes/links the Sankey draws. Built from what the data
// actually contains (never a fixed list), so every source and every status the account
// produces shows up, each carrying its count and its order value.
export const buildAlertsFlow = (data) => {
  const rows = data?.result || []

  const pairs = new Map() // service -> Map(status -> {count, volume})
  const serviceTotals = new Map()
  const statusTotals = new Map()
  const serviceVolumes = new Map()
  const statusVolumes = new Map()
  let totalVolume = 0

  const add = (map, key, amount) => map.set(key, (map.get(key) ?? 0) + amount)

  rows.forEach((row) => {
    const service = row?.alert_service
    const status = row?.alert_status
    if (!service || !status) return
    const count = Number(row?.count) || 0
    const volume = Number(row?.volume) || 0

    if (!pairs.has(service)) pairs.set(service, new Map())
    pairs.get(service).set(status, { count, volume })

    add(serviceTotals, service, count)
    add(statusTotals, status, count)
    add(serviceVolumes, service, volume)
    add(statusVolumes, status, volume)
    totalVolume += volume
  })

  const byCountDesc = (a, b) => b[1] - a[1]
  const known = SERVICE_ORDER.filter((id) => serviceTotals.has(id))
  // Anything the account produces that is not one of the three known networks (PayPal,
  // say) keeps the old volume ordering and follows them.
  const extra = [...serviceTotals.entries()]
    .filter(([id]) => !SERVICE_ORDER.includes(id))
    .sort(byCountDesc)
    .map(([id]) => id)
  const services = [...known, ...extra]
  const statuses = [...statusTotals.entries()].sort(byCountDesc).map(([id]) => id)

  // A single root carries the whole population, so the chart opens on a 100% anchor
  // and reads left to right: all alerts -> the network that raised them -> outcome.
  const nodes = [
    ...(services.length
      ? [{ id: "all", kind: "all", value: "All alerts", volume: totalVolume }]
      : []),
    ...services.map((id) => ({
      id: `service:${id}`,
      kind: "service",
      value: id,
      volume: serviceVolumes.get(id) ?? 0
    })),
    ...statuses.map((id) => ({
      id: `status:${id}`,
      kind: "status",
      value: id,
      volume: statusVolumes.get(id) ?? 0
    }))
  ]

  // Emit links source-major so each node's ribbons leave in a stable, uncrossed order.
  const links = services.map((service) => ({
    source: "all",
    target: `service:${service}`,
    value: serviceTotals.get(service),
    volume: serviceVolumes.get(service) ?? 0
  }))
  services.forEach((service) => {
    statuses.forEach((status) => {
      const cell = pairs.get(service)?.get(status)
      if (cell?.count) {
        links.push({
          source: `service:${service}`,
          target: `status:${status}`,
          value: cell.count,
          volume: cell.volume
        })
      }
    })
  })

  return { nodes, links }
}

function* getAlertsFlowSaga(action) {
  try {
    const response = yield call(services.getAlertsFlow, action.payload)
    if (response.ok && response.data) {
      yield put(Creators.getAlertsFlowSuccess(buildAlertsFlow(response.data)))
    } else {
      yield put(Creators.getAlertsFlowFailure())
    }
  } catch (e) {
    yield put(Creators.getAlertsFlowFailure())
  }
}

const actions = {
  getAlertOverviewDataActions: {
    request: Creators.getAlertOverviewDataRequest,
    success: Creators.getAlertOverviewDataSuccess,
    errors: Creators.getAlertOverviewDataFailure
  },
  getAlertsDescriptorDataActions: {
    request: Creators.getAlertsDescriptorDataRequest,
    success: Creators.getAlertsDescriptorDataSuccess,
    errors: Creators.getAlertsDescriptorDataFailure
  },
  getAlertsAccountDataActions: {
    request: Creators.getAlertsAccountDataRequest,
    success: Creators.getAlertsAccountDataSuccess,
    errors: Creators.getAlertsAccountDataFailure
  },
  getAlertsCountryDataActions: {
    request: Creators.getAlertsCountryDataRequest,
    success: Creators.getAlertsCountryDataSuccess,
    errors: Creators.getAlertsCountryDataFailure
  },
  getAlertsBankDataActions: {
    request: Creators.getAlertsBankDataRequest,
    success: Creators.getAlertsBankDataSuccess,
    errors: Creators.getAlertsBankDataFailure
  },
  getCreditsSummaryActions: {
    request: Creators.getCreditsSummaryRequest,
    success: Creators.getCreditsSummarySuccess,
    errors: Creators.getCreditsSummaryFailure
  },
  getAlertsCardTypeDataActions: {
    request: Creators.getAlertsCardTypeDataRequest,
    success: Creators.getAlertsCardTypeDataSuccess,
    errors: Creators.getAlertsCardTypeDataFailure
  },
  getUsersBySourceDataActions: {
    request: Creators.getUsersBySourceDataRequest,
    success: Creators.getUsersBySourceDataSuccess,
    errors: Creators.getUsersBySourceDataFailure
  },
  getAllBusinessAccountsListActions: {
    request: Creators.getAllBusinessAccountsListRequest,
    success: Creators.getAllBusinessAccountsListSuccess,
    errors: Creators.getAllBusinessAccountsListFailure
  },
  getDashboardStatisticsActions: {
    request: Creators.getDashboardStatisticsRequest,
    success: Creators.getDashboardStatisticsSuccess,
    errors: Creators.getDashboardStatisticsFailure
  }
}

const eventsOptions = {
  [constants.GET_ALERT_OVERVIEW_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertOverviewDataActions
  },
  [constants.GET_ALERTS_DESCRIPTOR_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertsDescriptorDataActions
  },
  [constants.GET_ALERTS_ACCOUNT_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertsAccountDataActions
  },
  [constants.GET_ALERTS_COUNTRY_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertsCountryDataActions
  },
  [constants.GET_ALERTS_BANK_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertsBankDataActions
  },
  [constants.GET_ALERTS_CARD_TYPE_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getAlertsCardTypeDataActions
  },
  [constants.GET_USERS_BY_SOURCE_DATA_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getUsersBySourceDataActions
  },
  [constants.GET_ALL_BUSINESS_ACCOUNTS_LIST_REQUEST]: {
    api: services.getAllBusinessAccountsList,
    actions: actions.getAllBusinessAccountsListActions
  },
  [constants.GET_CREDITS_SUMMARY_REQUEST]: {
    api: services.getDashboardData,
    actions: actions.getCreditsSummaryActions
  },
  [constants.GET_DASHBOARD_STATISTICS_REQUEST]: {
    api: services.getDashboardStatistics,
    actions: actions.getDashboardStatisticsActions
  }
}

export default function* apiSaga() {
  yield takeLatest(constants.GET_ALERT_OVERVIEW_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERT_OVERVIEW_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_ALERTS_DESCRIPTOR_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERTS_DESCRIPTOR_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_ALERTS_ACCOUNT_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERTS_ACCOUNT_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_ALERTS_COUNTRY_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERTS_COUNTRY_DATA_REQUEST])
  )

  yield takeLatest(constants.GET_ALERTS_BANK_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERTS_BANK_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_ALERTS_CARD_TYPE_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALERTS_CARD_TYPE_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_USERS_BY_SOURCE_DATA_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_USERS_BY_SOURCE_DATA_REQUEST])
  )
  yield takeLatest(constants.GET_ALL_BUSINESS_ACCOUNTS_LIST_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_ALL_BUSINESS_ACCOUNTS_LIST_REQUEST])
  )
  yield takeLatest(constants.GET_DASHBOARD_STATISTICS_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_DASHBOARD_STATISTICS_REQUEST])
  )
  yield takeLatest(constants.GET_CREDITS_SUMMARY_REQUEST, (action) =>
    apiGeneratorBase(action, eventsOptions[constants.GET_CREDITS_SUMMARY_REQUEST])
  )
  yield takeLatest(constants.GET_ALERTS_FLOW_REQUEST, getAlertsFlowSaga)
}
